import { captureException } from '@sentry/nextjs'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { segmentAnalytics } from 'lib/segmentAnalytics'
import { COOKIE_NAMES } from 'lib/constants'
import { checkErrorInstance } from 'utils/check-error-instance'
import { getIsInternalUser } from 'utils/getIsInternalUser'
import getUserAgentInfo from 'utils/getUserAgentInfo'

const trackPageViewViaTaboolaPixel = (urlString: string): void => {
  if (window._tfa) {
    window._tfa.push({
      notify: 'event',
      name: 'page_view',
      'item-url': urlString,
    })
  }
}

/**
 * @todo: This logic currently uses custom string parsing for query string
 *   parsing. We should refactor to instead use the native JS URL API.
 */
const trackPageViewInSegment = (urlString: string): void => {
  const hasNoUtmParam = !['?utm', '&utm'].find(
    (str) => window.location.search.indexOf(str) !== -1,
  )

  const ga_client_id = Cookies.get(COOKIE_NAMES.googleAnalyticsClientId) || null

  if (!urlString.includes('/secure') && hasNoUtmParam && !getIsInternalUser()) {
    segmentAnalytics.page(urlString, {
      pixel_ratio: window.devicePixelRatio,
      screen_height: window.screen.height,
      screen_width: window.screen.width,
      tz_offset: new Date().getTimezoneOffset(),
      userAgent: getUserAgentInfo(),
      ga_client_id,
    })
  }
}

const handleError = (originalError: unknown): void => {
  const error = checkErrorInstance(originalError)
  captureException(error)
}

/**
 * Custom hook to track page views on route change completion.
 *
 * Page views are currently tracked via Segment and Taboola Pixel.
 */
export const usePageViewTracking = (): void => {
  const router = useRouter()

  useEffect(() => {
    const handleRouteChange = async (urlString: string): Promise<void> => {
      try {
        trackPageViewInSegment(urlString)
      } catch (error) {
        handleError(error)
      }

      try {
        trackPageViewViaTaboolaPixel(urlString)
      } catch (error) {
        handleError(error)
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router])
}
